/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { autorun } from 'mobx';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getEventBus } from 'src/core/utils/eventBus';
import { ToastData } from 'src/luis/core/stores/ToastStore';
import { ModelJobStates } from 'src/modules/modelTraining/types/trainingTypes';

const eventBus = getEventBus();
const messages = defineMessages({
    inProgress: { id: 'customText.useAutoTaggingJobTracker.inProgress', defaultMessage: 'It may take some time.' },
    success: { id: 'customText.useAutoTaggingJobTracker.success', defaultMessage: 'Your changes are now effective.' },
    failDetails: { id: 'customText.useAutoTaggingJobTracker.failedDetails', defaultMessage: 'Auto-labeling job failed' },
    failTitle: { id: 'customText.useAutoTaggingJobTracker.failed', defaultMessage: "'{ modelName }' auto-labeling job failed" },
    successTitle: { id: 'customText.useAutoTaggingJobTracker.completed', defaultMessage: "'{ modelName }' auto-labeling job completed" },
    inProgressTitle: {
        id: 'customText.useAutoTaggingJobTracker.inProgressTitle',
        defaultMessage: "'{ modelName }' auto-labeling job in progress"
    }
});

const getToastDataFromJob = ({
    localize,
    modelName,
    autotaggingJobStatus
}: {
    modelName: string;
    autotaggingJobStatus: ModelJobStates;
    localize: ReactIntl.InjectedIntl['formatMessage'];
}): ToastData => {
    switch (autotaggingJobStatus) {
        case 'notStarted':
        case 'inProgress':
            return {
                status: 'running',
                title: localize(messages.inProgressTitle, { modelName })
            };
        case 'succeeded':
            return {
                status: 'completed',
                content: localize(messages.success),
                title: localize(messages.successTitle, { modelName })
            };
        case 'failed':
            return {
                status: 'failed',
                content: localize(messages.failDetails),
                title: localize(messages.failTitle, { modelName })
            };
        default:
            return {};
    }
};

export const useAutoTaggingJobTracker = () => {
    const { localize } = useLocalization();
    const toastIdRef = React.useRef<string>();
    const { rootStore, dispatcher } = useGlobalState();
    const { currentCustomTextProjectStore } = rootStore;

    React.useEffect(() => {
        if (!currentCustomTextProjectStore) {
            return;
        }

        const disposer = autorun(() => {
            const toastGptJob = currentCustomTextProjectStore.gptJobsStoreHouse.findById(toastIdRef.current);
            const toastAutotaggingJob = currentCustomTextProjectStore.autoTaggingJobStoreHouse.findById(toastIdRef.current);
            const runningGptJob = currentCustomTextProjectStore.gptJobsStoreHouse.items.find(
                j => j.status === 'notStarted' || j.status === 'inProgress'
            );
            const runningAutotaggingJob = currentCustomTextProjectStore.autoTaggingJobStoreHouse.items.find(
                j => j.status === 'notStarted' || j.status === 'inProgress'
            );

            if (!runningAutotaggingJob && !toastAutotaggingJob && !runningGptJob && !toastGptJob) {
                return;
            }

            const currentGptJob = runningGptJob ?? toastGptJob;
            const currentAutotaggingJob = runningAutotaggingJob ?? toastAutotaggingJob;

            const toastData = getToastDataFromJob({
                localize,
                modelName: currentAutotaggingJob?.trainedModelLabel ?? `Azure OpenAI's GPT`,
                autotaggingJobStatus: currentAutotaggingJob?.status ?? currentGptJob?.status
            });

            if (
                (!toastIdRef.current && (currentAutotaggingJob || currentGptJob)) ||
                (toastIdRef.current !== currentAutotaggingJob?.id && toastIdRef.current !== currentGptJob?.id)
            ) {
                toastIdRef.current = currentAutotaggingJob?.id ?? currentGptJob?.id;
                eventBus.emit('addToast', { id: toastIdRef.current, ...toastData });
            } else {
                eventBus.emit('updateToast', { id: toastIdRef.current, toast: toastData });
                if (currentAutotaggingJob?.status === 'succeeded') {
                    dispatcher.dispatch('getAutotaggingQuota', { projectName: currentCustomTextProjectStore.name });
                    dispatcher.dispatch('getExtractionExportProject', { projectName: currentCustomTextProjectStore.name });
                    eventBus.emit('notifyAutotaggingSucceeded');
                }

                if (currentGptJob?.status === 'succeeded') {
                    eventBus.emit('notifyAutotaggingSucceeded');
                }
            }
        });

        return () => {
            disposer();
            if (rootStore.toastStoreHouse.findById(toastIdRef.current)) {
                dispatcher.dispatch('removeToast', { id: toastIdRef.current });
            }
        };
    }, [currentCustomTextProjectStore]);
};
